import { useCallback, useRef, useState } from 'react'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { useFocusRequest } from '../../platform/a11y/useFocusRequest'
import { useToast } from '../../platform/feedback/ToastProvider'
import { emit } from '../../platform/analytics/analytics'
import { REQUIRES_RECENT_LOGIN, authErrorFor } from '../../auth/errors'
import { AuthFailure, type EnrolledFactor } from '../../auth/port'
import { useAuth } from '../../auth/useAuth'
import { AddTotp } from './AddTotp'
import { AddPhone } from './AddPhone'
import { ReauthDialog } from './ReauthDialog'
import { DataPrivacy } from './DataPrivacy'
import { syncFactors } from '../../api/mfa'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { EmailCodesSheet } from './EmailCodesSheet'
import { RecoveryCodesSheet } from './RecoveryCodesSheet'
import { STRENGTH_LABEL, securityStrength } from './securityStrength'
import { useMfaBackend } from './useMfaBackend'
import './security.css'

/** The kind's name, shared with Profile's two-step summary so the two never disagree. */
export function kindName(kind: EnrolledFactor['kind']): string {
  return kind === 'totp' ? 'Authenticator app' : 'Text message'
}

/** What to call a factor in a list. The masked number is what tells two apart. */
function labelFor(factor: EnrolledFactor): string {
  const name = kindName(factor.kind)
  return factor.label !== null && factor.label !== name ? `${name} · ${factor.label}` : name
}

/** `2026-08-07T…` → `7 August 2026`. Empty where Firebase reported nothing. */
function addedOn(factor: EnrolledFactor): string | null {
  if (factor.enrolledAt === null) return null
  const at = new Date(factor.enrolledAt)
  if (Number.isNaN(at.getTime())) return null
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

type Adding = 'totp' | 'phone' | null

/**
 * Settings › Security — the only place a second factor is added or removed.
 *
 * **A hybrid of two sources, and which is which matters.**
 *
 * - **Sign-in factors live in Firebase**: the authenticator app (TOTP) and SMS.
 *   Sign-in checks them, they are listed from the live Firebase user, and
 *   `AddTotp` / `AddPhone` enrol them. Firebase allows one authenticator app
 *   per account.
 * - **Backup methods live in the backend** (`/api/v1/mfa/*`): email codes and
 *   recovery codes. The backend asks for them to confirm sensitive changes
 *   (step-up) and never at sign-in, so the copy never says they protect sign-in.
 *   They are read through `useMfaBackend` inside `<Async>`; a failure there is
 *   that card's error and never hides the Firebase rows.
 *
 * **Two gates, two mechanisms.**
 *
 * - *Removing a Firebase factor* re-authenticates in the browser. The LAST
 *   factor always does, whatever the session's age: `ReauthDialog` runs
 *   `reauthenticate` (a password prompt, or the provider's popup) and
 *   `removeFactor` is not reached until it resolves. "Last" is decided by the
 *   adapter from a LIVE read at removal time, so a stale list in another tab
 *   cannot skip it. An enrolment Firebase refuses with `auth/requires-recent-login`
 *   opens the same prompt and then repeats the refused step.
 * - *Changing a backend factor* (regenerate recovery codes, turn on email codes)
 *   meets the backend's own gates: `reauth_required` opens the same
 *   `ReauthDialog`, and `step_up_required` opens `StepUpSheet`, which completes
 *   the backend's step-up and hands back a single-use token for one retry. The
 *   backend enforces both; the browser only collects the proof.
 *
 * **Rows with no endpoint behind them are left out (M13-R19):** password
 * "last changed", auto-lock, passkeys and a numeric security score. The
 * strength is a level word (`securityStrength`), and it counts backup methods as
 * well as sign-in factors, so it is shown as account security and not as a
 * property of the sign-in card.
 *
 * **Starts at `<h2>`.** `ChromeLayout` renders the page name as the document's
 * only `<h1>`; a screen that titles itself again gives every page two.
 */
export function Security() {
  const auth = useAuth()
  /**
   * Read once and re-read explicitly, rather than called during render.
   *
   * `enrolledFactors()` reads Firebase's live user object — external mutable
   * state — and calling it in the render body made the re-read depend on
   * whatever else happened to change state that tick. It did work, but only
   * incidentally: removing the `key={tick}` remount AND the tick itself left
   * every test green, because `setAdding(null)` was quietly doing the job.
   * Machinery that is load-bearing by accident reads as deliberate to the next
   * person and breaks silently when the accident is tidied away.
   *
   * The refresh is now the only thing that moves this, and the two tests for
   * "the list reflects an enrolment / a removal" fail without it.
   */
  const [factors, setFactors] = useState<EnrolledFactor[]>(() => auth.enrolledFactors())
  const [adding, setAdding] = useState<Adding>(null)
  const [removing, setRemoving] = useState<string | null>(null)

  /**
   * Firebase allows exactly ONE authenticator app per account — measured, not
   * assumed: enrolling a second returns `auth/maximum-second-factor-count-
   * exceeded` from `mfaEnrollment:finalize`, with one TOTP factor already on
   * the account.
   *
   * So the action is not offered. It was, and it failed politely — but a
   * control whose only possible outcome is an error is the wrong shape, and the
   * error's own wording ("as many verification methods as an account can hold")
   * reads as nonsense to someone holding exactly one.
   */
  const hasAuthenticator = factors.some((f) => f.kind === 'totp')

  /**
   * The shared queue, not a region of this screen's own — #194.
   *
   * **Why anything is announced at all:** the two mutations this screen exists
   * for were both SILENT, and both left focus on `<body>` — measured in Chrome.
   * A row appearing or disappearing is the only feedback there was, which a
   * screen-reader user gets neither of: the list is not a live region, and the
   * control that was pressed is unmounted or disabled by the same commit that
   * changes it. So the outcome is announced, and focus is put somewhere
   * deliberate rather than at the top of the document.
   *
   * **Why through the queue rather than a region here:** `.security__status`
   * and its `useAnnouncement` are gone, and moving ALL THREE outcomes is what
   * makes that true. Moving one would have retired nothing — the region stays
   * mounted for the other two and the app gains the queue's two on top, which
   * is seventeen live regions becoming nineteen.
   */
  const { show } = useToast()
  /**
   * The heading, as the place focus lands after a mutation.
   *
   * NOT the "Add authenticator app" button, which was the first attempt: that
   * button disappears the moment an authenticator exists, so enrolling one
   * removed its own focus target and dropped the user back to `<body>`. The
   * heading is the one element on this screen that is always here, and landing
   * at the top of the section that just changed is the ordinary pattern for a
   * region-level update.
   *
   * `tabIndex={-1}` makes it focusable without putting it in the tab order.
   */
  const headingRef = useRef<HTMLHeadingElement>(null)
  const focus = useFocusRequest({ heading: headingRef })

  const refresh = useCallback(
    (said: string) => {
      setFactors(auth.enrolledFactors())
      setAdding(null)
      // `success`, and politely: an enrolment or a removal that WORKED is not
      // an interruption. A FAILED removal does not come through here at all —
      // it stays in the dialog that asked for it, which is assertive and does
      // not retire on a clock. An error that vanishes is one the user may never
      // have read.
      show({ kind: 'success', message: said })
      // The enrolment panel that had focus is unmounted by this very update.
      // The target may not exist — adding an authenticator removes its own
      // button — and `useFocusRequest` spends the request either way, which is
      // exactly the case its tests pin.
      focus('heading')
      // Tell the backend what Firebase now holds. Fire-and-forget: the factor
      // IS enrolled either way, and a failed sync must not read as a failed
      // enrolment. `syncFactors` reports its own outcome.
      void syncFactors()
    },
    [auth, focus, show],
  )

  /**
   * The factor whose removal is being confirmed, or `null`.
   *
   * The state IS the dialog's `open`. A separate boolean beside it would make
   * `open && confirming === null` representable, which is a dialog asking about
   * nothing.
   */
  const [confirming, setConfirming] = useState<EnrolledFactor | null>(null)
  /**
   * The LAST factor, whose removal waits on a fresh re-authentication — or
   * `null`. Its own state rather than a flag on `confirming`: the two dialogs
   * are different components, and `confirming !== null && reauthRemoving !==
   * null` would be two dialogs asking about the same click.
   */
  const [reauthRemoving, setReauthRemoving] = useState<EnrolledFactor | null>(null)
  /**
   * An enrolment step Firebase refused for want of a recent login, kept as the
   * function that repeats it. A function in state, so it is wrapped in a
   * thunk at both ends.
   */
  const [enrolRetry, setEnrolRetry] = useState<(() => void) | null>(null)
  const needsReauth = useCallback((retry: () => void) => setEnrolRetry(() => retry), [])

  /**
   * What is lost, in the factor's own terms.
   *
   * Two sentences, because removing the LAST factor is a materially different
   * loss from removing one of several and "Are you sure?" says neither. Both
   * survived the move off `window.confirm` unchanged — they are what #194 asks a
   * confirmation to do, and they were already right.
   */
  function lossFrom(factor: EnrolledFactor): string {
    return factors.length === 1
      ? 'Remove your only verification method? Your account will be protected by your password alone.'
      : `Remove ${labelFor(factor).toLowerCase()}? You'll no longer be asked for codes from it.`
  }

  /**
   * The removal itself, run by the dialog.
   *
   * It THROWS rather than catching: `ConfirmDialog` awaits this, and a rejection
   * is how it knows to stay open and show the reason. The `catch` that used to
   * live here — set an error, refocus the heading — is what the dialog's failure
   * state replaces, so `FormError` and the focus dance are gone from this path
   * rather than duplicated by it.
   *
   * `setConfirming(null)` on success is deliberate and not optional: `open` is
   * controlled, so the dialog cannot close itself, and the work that succeeded
   * is this screen's.
   */
  /**
   * The plain confirm's action.
   *
   * **It does not decide whether this is the last factor.** The adapter does,
   * from a live read, and refuses with `requires-recent-login` when it is — or
   * when Firebase's own recent-login window has lapsed on an old session, which
   * is the ordinary case for one of several factors. Either way the answer is
   * the same: hand over to the re-authentication prompt, in the app, instead of
   * telling the user to sign out and back in. The list is re-read first, since
   * the adapter just reloaded it and the prompt's wording depends on whether
   * this is now the only method.
   */
  async function confirmPlain(factor: EnrolledFactor): Promise<void | 'handed_over'> {
    try {
      await removeConfirmed(factor)
    } catch (thrown) {
      if (thrown instanceof AuthFailure && thrown.detail.code === REQUIRES_RECENT_LOGIN) {
        setFactors(auth.enrolledFactors())
        setConfirming(null)
        setReauthRemoving(factor)
        // Not confirmed yet: the prompt emits its own outcome for this removal.
        return 'handed_over'
      }
      throw thrown
    }
  }

  async function removeConfirmed(factor: EnrolledFactor) {
    setRemoving(factor.id)
    try {
      await auth.removeFactor(factor.id)
      emit('mfa_factor_removed', { factor_type: factor.kind })
      setConfirming(null)
      setReauthRemoving(null)
      refresh(`${labelFor(factor)} removed.`)
    } finally {
      setRemoving(null)
    }
  }

  const backend = useMfaBackend()
  const backendData = backend.status === 'success' ? backend.data : null
  const strength = securityStrength({
    totp: hasAuthenticator,
    phone: factors.some((f) => f.kind === 'phone'),
    email: backendData?.emailConfigured ?? false,
    recoveryRemaining: backendData?.recoveryRemaining ?? 0,
  })
  const [emailSheet, setEmailSheet] = useState(false)
  const [recoverySheet, setRecoverySheet] = useState(false)

  return (
    <section className="security">
      <h2 className="security__title" tabIndex={-1} ref={headingRef}>
        Two-factor authentication
      </h2>
      <p className="security__lede">
        Your authenticator app and phone are asked for when you sign in. Email codes and recovery
        codes confirm sensitive changes — they don&apos;t sign you in.
      </p>

      {/* The outcome region that stood here is gone — it is the queue's now. */}

      {/* Outside both cards on purpose: the level counts backup methods too, so
          inside "Sign-in verification" it would say sign-in got stronger. */}
      <p className="security__strength">
        Account security: <strong>{STRENGTH_LABEL[strength]}</strong>
      </p>

      <div className="security__card">
        <div className="security__cardhead">
          <span className="security__cardtitle">Sign-in verification</span>
          <span className={`security__pill${factors.length > 0 ? ' security__pill--on' : ''}`}>
            {factors.length > 0 ? 'On' : 'Off'}
          </span>
        </div>

        {factors.length === 0 ? (
          // Names the missing thing and offers one action — the empty-state rule.
          <p className="security__empty">
            You haven&apos;t added a verification method yet. Your account is protected by your
            password alone.
          </p>
        ) : (
          <ul className="security__list">
            {factors.map((factor) => {
              const added = addedOn(factor)
              return (
                <li className="security__row" key={factor.id}>
                  <div className="security__factor">
                    <span className="security__name">{labelFor(factor)}</span>
                    {added !== null && <span className="security__added">Added {added}</span>}
                  </div>
                  <Button
                    variant="outline"
                    // `page`, not `inline`: the 24px target is documented for
                    // dense DESKTOP rows and this list is read on phones.
                    size="page"
                    disabled={removing !== null}
                    // The last one steps up; one of several keeps the plain confirm.
                    onClick={() =>
                      factors.length === 1 ? setReauthRemoving(factor) : setConfirming(factor)
                    }
                  >
                    Remove
                  </Button>
                </li>
              )
            })}
          </ul>
        )}

        {adding === null ? (
          <div className="security__actions">
            {hasAuthenticator ? (
              // Says WHY rather than showing a control that cannot work.
              <p className="security__limit">
                You already have an authenticator app. Remove it first if you want to use a
                different one.
              </p>
            ) : (
              <Button variant="primary" size="page" onClick={() => setAdding('totp')}>
                Add authenticator app
              </Button>
            )}
            <Button variant="outline" size="page" onClick={() => setAdding('phone')}>
              Add phone number
            </Button>
          </div>
        ) : (
          <div className="security__panel">
            {adding === 'totp' ? (
              <AddTotp
                onDone={() => refresh('Authenticator app added.')}
                onCancel={() => setAdding(null)}
                onNeedsReauth={needsReauth}
              />
            ) : (
              <AddPhone
                onDone={() => refresh('Phone number added.')}
                onCancel={() => setAdding(null)}
                onNeedsReauth={needsReauth}
              />
            )}
          </div>
        )}
      </div>

      <div className="security__card">
        <div className="security__cardhead">
          <span className="security__cardtitle">Backup methods</span>
        </div>
        {/* The backend half. Its failure is this block's error state; the
            Firebase factors above never wait on it. */}
        <Async
          state={backend}
          surface="security-mfa"
          skeleton={<Skeleton />}
          empty={{ title: 'No backup methods', body: 'Nothing to show yet.' }}
        >
          {(mfa) => (
            <ul className="security__list security__list--flush">
              <li className="security__row">
                <div className="security__factor">
                  <span className="security__name">Email codes</span>
                  <span className="security__added">
                    {mfa.emailConfigured
                      ? 'On. Confirms sensitive changes, not sign-in.'
                      : 'A backup for confirming sensitive changes. Not used at sign-in.'}
                  </span>
                </div>
                {mfa.emailConfigured ? (
                  <span className="security__pill security__pill--on">On</span>
                ) : (
                  <Button variant="outline" size="page" onClick={() => setEmailSheet(true)}>
                    Add email codes
                  </Button>
                )}
              </li>
              <li className="security__row">
                <div className="security__factor">
                  <span className="security__name">Recovery codes</span>
                  {mfa.enabled ? (
                    <span
                      className={`security__added${mfa.recoveryRemaining <= 3 ? ' security__added--warn' : ''}`}
                    >
                      {mfa.recoveryRemaining} of {mfa.recoveryTotal} remaining
                      {mfa.recoveryRemaining <= 3 ? ' — regenerate soon' : ''}
                    </span>
                  ) : (
                    // The backend makes codes only for an account whose backup
                    // methods are on (it answers 400 otherwise), so no control.
                    <span className="security__added">
                      Available once email codes are on. They confirm sensitive changes, not sign-in.
                    </span>
                  )}
                </div>
                {mfa.enabled && (
                  <Button variant="outline" size="page" onClick={() => setRecoverySheet(true)}>
                    Regenerate
                  </Button>
                )}
              </li>
            </ul>
          )}
        </Async>
      </div>

      <RecoveryCodesSheet
        open={recoverySheet}
        onClose={() => setRecoverySheet(false)}
        remaining={backendData?.recoveryRemaining ?? 0}
      />
      <EmailCodesSheet open={emailSheet} onClose={() => setEmailSheet(false)} />

      <ReauthDialog
        open={reauthRemoving !== null}
        mode="remove_factor"
        title="Confirm it's you"
        body={reauthRemoving === null ? '' : lossFrom(reauthRemoving)}
        confirmLabel="Remove"
        pendingLabel="Removing…"
        destructive
        onCancel={() => setReauthRemoving(null)}
        onReauthed={() => removeConfirmed(reauthRemoving as EnrolledFactor)}
        restoreFocusTo={headingRef}
      />
      <ReauthDialog
        open={enrolRetry !== null}
        mode="enrol_factor_reauth"
        title="Confirm it's you"
        body="For your security, confirm your identity again before adding a verification method."
        confirmLabel="Continue"
        pendingLabel="Checking…"
        onCancel={() => setEnrolRetry(null)}
        onReauthed={async () => {
          const retry = enrolRetry
          setEnrolRetry(null)
          retry?.()
        }}
      />

      {/* Rendered once for the screen rather than once per row: a dialog per
          factor would mount N portals, N focus traps and N inert sweeps for a
          list where at most one can ever be open. `confirming` is which. */}
      <ConfirmDialog
        open={confirming !== null}
        mode="remove_factor"
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirmPlain(confirming as EnrolledFactor)}
        // The specific translation this screen already owned, handed to the
        // dialog instead of being re-implemented inside it. Without it a
        // rejection renders as a generic sentence, which CLAUDE.md names as a
        // defect wherever something specific is possible.
        errorFor={(thrown) =>
          (thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')).message
        }
        title="Remove verification method"
        body={confirming === null ? '' : lossFrom(confirming)}
        confirmLabel="Remove"
        pendingLabel="Removing…"
        // Where focus goes when the Remove button that opened this is gone by
        // the time it closes — which is exactly what a successful removal does
        // to it. `.focus()` on a detached node is a silent no-op.
        restoreFocusTo={headingRef}
        destructive
      />
      <DataPrivacy />
    </section>
  )
}
